import { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useProjects } from "@/hooks/useProjects";
import { AppHeader } from "@/components/AppHeader";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Check, Plus, ArrowRight, ArrowLeft, PanelRightClose, PanelRightOpen, MoreHorizontal, Trash2, ClipboardList, ZoomIn, ZoomOut } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useRuns } from "@/hooks/useRuns";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { ProjectSidebar } from "@/components/project/ProjectSidebar";
import type { SynthesisStep } from "@/types/chemistry";
import { StepCard } from "@/components/project/StepCard";


// SVG draw-in animation styles
const svgAnimStyles = `
@keyframes draw-line {
  from { stroke-dashoffset: var(--path-length); }
  to { stroke-dashoffset: 0; }
}
@keyframes draw-line-reverse {
  from { stroke-dashoffset: calc(-1 * var(--path-length)); }
  to { stroke-dashoffset: 0; }
}
@keyframes fade-arrow {
  from { opacity: 0; transform: scale(0.5); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes node-enter {
  from { opacity: 0; transform: translateX(8px); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes node-enter-forward {
  from { opacity: 0; transform: translateX(-8px); }
  to { opacity: 1; transform: translateX(0); }
}
.svg-connector-path {
  animation: draw-line 0.5s ease-out forwards;
}
.svg-connector-path-forward {
  animation: draw-line-reverse 0.5s ease-out forwards;
}
.svg-arrow-head {
  opacity: 0;
  animation: fade-arrow 0.2s ease-out 0.4s forwards;
  transform-origin: center;
}
.tree-node-enter {
  animation: node-enter 0.3s ease-out forwards;
}
.tree-node-enter-forward {
  animation: node-enter-forward 0.3s ease-out forwards;
}
`;

const PlusButton = ({ onClick }: { onClick: () => void }) => (
  <button
    onClick={onClick}
    className="border border-dashed border-muted-foreground/20 rounded-full w-4 h-4 flex items-center justify-center hover:border-primary hover:text-primary transition-colors text-muted-foreground/30 bg-background"
  >
    <Plus className="h-2.5 w-2.5" />
  </button>
);

const BranchDropdownButton = ({ onBranchBackward, onBranchForward }: { onBranchBackward: () => void; onBranchForward: () => void }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button className="border border-dashed border-muted-foreground/20 rounded-full w-4 h-4 flex items-center justify-center hover:border-primary hover:text-primary transition-colors text-muted-foreground/30 bg-background">
        <Plus className="h-2.5 w-2.5" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="bg-popover min-w-[140px]">
      <DropdownMenuItem onClick={onBranchBackward} className="text-xs gap-2">
        <ArrowLeft className="h-3.5 w-3.5" />
        Branch backward
      </DropdownMenuItem>
      <DropdownMenuItem onClick={onBranchForward} className="text-xs gap-2">
        <ArrowRight className="h-3.5 w-3.5" />
        Branch forward
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const ProjectView = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { getProject, updateStep, addStep, addStepAfter, addBranch, deleteStep, addStepBefore, addRootStep, addBranchForward } = useProjects();
  const project = getProject(projectId!);
  const { getRunsForProject } = useRuns();
  const projectRuns = project ? getRunsForProject(projectId!) : [];

  const [selectingForRun, setSelectingForRun] = useState(false);
  const [selectedStepIds, setSelectedStepIds] = useState<string[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // direction toggle removed — now handled per-button via dropdown
  const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5] as const;
  const [zoomIndex, setZoomIndex] = useState(2);
  const zoom = ZOOM_LEVELS[zoomIndex];

  // Canvas drag-to-pan
  const canvasRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  // Track steps added via forward actions for animation direction
  const forwardStepIds = useRef<Set<string>>(new Set());

  // Auto-scroll to newly added step
  const scrollToStepId = useRef<string | null>(null);
  const scrollIsBranch = useRef(false);
  const prevStepCount = useRef(project?.steps.length ?? 0);

  // Auto-scroll to the right on mount so the root/target is visible
  useEffect(() => {
    const el = canvasRef.current;
    if (el) {
      el.scrollLeft = el.scrollWidth;
    }
  }, [projectId]);

  // Keyboard zoom shortcuts (Ctrl/Cmd + Plus/Minus)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key === '=' || e.key === '+') {
        e.preventDefault();
        setZoomIndex(i => Math.min(ZOOM_LEVELS.length - 1, i + 1));
      } else if (e.key === '-') {
        e.preventDefault();
        setZoomIndex(i => Math.max(0, i - 1));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Scroll to newly added step after render
  const stepCount = project?.steps.length ?? 0;
  const animateCanvasTo = useCallback((el: HTMLDivElement, left: number, top: number, duration = 360) => {
    const startLeft = el.scrollLeft;
    const startTop = el.scrollTop;
    const deltaLeft = left - startLeft;
    const deltaTop = top - startTop;
    const start = performance.now();

    const ease = (t: number) => 1 - Math.pow(1 - t, 3); // easeOutCubic

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const k = ease(t);
      el.scrollLeft = startLeft + deltaLeft * k;
      el.scrollTop = startTop + deltaTop * k;
      if (t < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (scrollToStepId.current && stepCount > prevStepCount.current) {
      const el = canvasRef.current;
      const targetId = scrollToStepId.current;
      const isBranch = scrollIsBranch.current;
      scrollToStepId.current = null;
      scrollIsBranch.current = false;
      if (el) {
        // Double rAF to let layout settle after content grows
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const node = el.querySelector(`[data-step-id="${targetId}"]`) as HTMLElement | null;
            if (node) {
              const MIN_SCROLL_DELTA = 96;
              const nodeRect = node.getBoundingClientRect();
              const elRect = el.getBoundingClientRect();
              // Target the card center within the node's subtree, not the node element center
              const cardOffsetY = node.offsetHeight > CARD_H + LABEL_H ? (node.offsetHeight - CARD_H - LABEL_H) / 2 + LABEL_H + CARD_H / 2 : LABEL_H + CARD_H / 2;
              const targetScrollTop = el.scrollTop + (nodeRect.top - elRect.top) + cardOffsetY - elRect.height / 2;
              const maxScrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
              const nextTop = Math.min(maxScrollTop, Math.max(0, targetScrollTop));

              if (isBranch) {
                // Branch add: only scroll vertically to center the new node
                animateCanvasTo(el, el.scrollLeft, nextTop, 360);
              } else {
                const targetScrollLeft = el.scrollLeft + (nodeRect.left - elRect.left) - LEFT_SCROLL_PADDING;
                const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
                const nextLeft = Math.min(maxScrollLeft, Math.max(0, targetScrollLeft));

                // Keep perceived motion consistent near the edge
                const currentLeft = el.scrollLeft;
                const finalLeft = Math.abs(nextLeft - currentLeft) < MIN_SCROLL_DELTA
                  ? Math.max(0, currentLeft - MIN_SCROLL_DELTA)
                  : nextLeft;

                animateCanvasTo(el, finalLeft, nextTop, 360);
              }
            }
          });
        });
      }
    }
    prevStepCount.current = stepCount;
  }, [stepCount, animateCanvasTo]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    const el = canvasRef.current;
    if (!el) return;
    if (e.button !== 0) return;
    // Don't intercept clicks on interactive elements (buttons, links, inputs, or step nodes)
    const target = e.target as HTMLElement;
    if (target.closest('button, a, input, select, textarea, [role="button"], [role="menuitem"], [role="menu"], [data-radix-popper-content-wrapper], [data-step-card]')) return;
    isDragging.current = true;
    hasDragged.current = false;
    dragStart.current = { x: e.clientX, y: e.clientY, scrollLeft: el.scrollLeft, scrollTop: el.scrollTop };
    el.style.cursor = 'grabbing';
    el.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current || !canvasRef.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    canvasRef.current.scrollLeft = dragStart.current.scrollLeft - dx;
    canvasRef.current.scrollTop = dragStart.current.scrollTop - dy;
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    isDragging.current = false;
    if (canvasRef.current) canvasRef.current.style.cursor = 'grab';
  }, []);

  const toggleStepSelection = (stepId: string) => {
    setSelectedStepIds((prev) =>
      prev.includes(stepId) ? prev.filter((id) => id !== stepId) : [...prev, stepId]
    );
  };

  const enterSelectionMode = () => {
    setSelectedStepIds([]);
    setSelectingForRun(true);
  };

  const selectAll = () => {
    if (project) setSelectedStepIds(project.steps.map((s) => s.id));
  };

  const cancelSelection = () => {
    setSelectingForRun(false);
    setSelectedStepIds([]);
  };

  const confirmSelection = () => {
    navigate(`/project/${projectId}/run/new`, { state: { selectedStepIds } });
    setSelectingForRun(false);
  };

  if (!project) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <div className="flex items-center justify-center py-20">
          <p className="text-muted-foreground">Compound not found</p>
        </div>
      </div>
    );
  }

  // Build tree structure
  const childrenMap: Record<string, SynthesisStep[]> = {};
  const roots: SynthesisStep[] = [];
  const rootIds = new Set<string>();
  project.steps.forEach((s) => {
    if (!s.parentId) {
      roots.push(s);
      rootIds.add(s.id);
    } else {
      if (!childrenMap[s.parentId]) childrenMap[s.parentId] = [];
      childrenMap[s.parentId].push(s);
    }
  });

  // Connector constants
  const CARD_W = 280;
  const CARD_H = 180;
  const LABEL_H = 28;
  const ROW_GAP = 24;
  const CONNECTOR_W = 48;
  const LEFT_SCROLL_PADDING = 120;

  const getSubtreeHeight = (stepId: string): number => {
    const ch = childrenMap[stepId] || [];
    if (ch.length === 0) return LABEL_H + CARD_H;
    const childHeights = ch.map((c) => getSubtreeHeight(c.id));
    const totalChildHeight = childHeights.reduce((a, b) => a + b, 0) + (ch.length - 1) * ROW_GAP;
    return Math.max(LABEL_H + CARD_H, totalChildHeight);
  };

  // Returns the actual Y center of a node's card within its subtree
  const getCardCenterY = (stepId: string): number => {
    const subtreeH = getSubtreeHeight(stepId);
    const marginTop = Math.max(0, (subtreeH - CARD_H - LABEL_H) / 2);
    return marginTop + LABEL_H + CARD_H / 2;
  };

  const renderNode = (step: SynthesisStep, isBranchChild = false): JSX.Element => {
    const children = childrenMap[step.id] || [];
    const isLeaf = children.length === 0;
    const hasBranch = children.length > 1;
    const isForward = forwardStepIds.current.has(step.id);
    // Clear from set after first render so animation doesn't replay
    if (isForward) forwardStepIds.current.delete(step.id);
    const nodeAnimClass = isForward ? 'tree-node-enter-forward' : 'tree-node-enter';

    return (
      <div key={step.id} data-step-id={step.id} className={`flex flex-row-reverse items-start ${nodeAnimClass}`}>

        <div
          className="flex items-start shrink-0"
          style={{ minHeight: getSubtreeHeight(step.id) }}
        >
          <div style={{ marginTop: Math.max(0, (getSubtreeHeight(step.id) - CARD_H - LABEL_H) / 2) }}>
            <StepCard
              step={step}
              selecting={selectingForRun}
              selected={selectedStepIds.includes(step.id)}
              zoom={zoom}
              isTarget={rootIds.has(step.id)}
              onOpen={
                selectingForRun
                  ? () => toggleStepSelection(step.id)
                  : () => navigate(`/project/${project.id}/step/${step.id}`)
              }
              onToggleComplete={() =>
                updateStep(project.id, step.id, {
                  completed: !step.completed,
                  status: step.completed ? "planning" : "complete",
                })
              }
            />
          </div>
        </div>

        {children.length > 0 && (
          <div className="flex flex-row-reverse items-start">
            {hasBranch ? (() => {
              const childHeights = children.map((c) => getSubtreeHeight(c.id));
              const totalH = childHeights.reduce((a, b) => a + b, 0) + (children.length - 1) * ROW_GAP;
              const parentCenterY = getCardCenterY(step.id);
              let accY = 0;
              const childCenters = childHeights.map((h, i) => {
                const cy = accY + getCardCenterY(children[i].id);
                accY += h + ROW_GAP;
                return cy;
              });

              return (
                <div className="flex flex-row-reverse items-start" style={{ height: totalH }}>
                  <div className="relative shrink-0" style={{ width: CONNECTOR_W, height: totalH }}>
                    <svg width={CONNECTOR_W} height={totalH} className="shrink-0" style={{ overflow: 'visible' }}>
                      <line
                        x1={CONNECTOR_W} y1={parentCenterY} x2={CONNECTOR_W / 2} y2={parentCenterY}
                        stroke="hsl(var(--muted-foreground) / 0.3)"
                        strokeWidth="1.5"
                        className="svg-connector-path"
                        style={{ '--path-length': `${CONNECTOR_W / 2}`, strokeDasharray: CONNECTOR_W / 2, strokeDashoffset: CONNECTOR_W / 2 } as React.CSSProperties}
                      />
                      <polygon
                        points={`${CONNECTOR_W},${parentCenterY} ${CONNECTOR_W - 5},${parentCenterY - 3} ${CONNECTOR_W - 5},${parentCenterY + 3}`}
                        fill="hsl(var(--muted-foreground) / 0.3)"
                        className="svg-arrow-head"
                      />
                      {childCenters.map((cy, i) => {
                        const midX = CONNECTOR_W / 2;
                        const endY = cy;
                        const startY = parentCenterY;
                        const R = 4;
                        const dy = endY - startY;
                        const absDy = Math.abs(dy);
                        const r = Math.min(R, absDy / 2, midX);
                        let d: string;
                        if (absDy < 0.5) {
                          d = `M ${midX} ${startY} L ${0} ${endY}`;
                        } else {
                          const signY = dy > 0 ? 1 : -1;
                          d = `M ${midX} ${startY} L ${midX} ${endY - signY * r} Q ${midX} ${endY} ${midX - r} ${endY} L ${0} ${endY}`;
                        }
                        const pathLen = absDy + midX;
                        return (
                          <path
                            key={children[i].id}
                            d={d}
                            fill="none"
                            stroke="hsl(var(--muted-foreground) / 0.3)"
                            strokeWidth="1.5"
                            className="svg-connector-path"
                            style={{ '--path-length': `${pathLen}`, strokeDasharray: pathLen, strokeDashoffset: pathLen } as React.CSSProperties}
                          />
                        );
                      })}
                    </svg>
                    <button
                      onClick={() => { scrollIsBranch.current = true; scrollToStepId.current = addBranch(project.id, step.id); }}
                      className="absolute border border-dashed border-muted-foreground/20 rounded-full w-4 h-4 flex items-center justify-center hover:border-primary hover:text-primary transition-colors text-muted-foreground/30 bg-background"
                      style={{ left: CONNECTOR_W / 2 - 8, top: parentCenterY - 8 }}
                      title="Add parallel branch"
                    >
                      <Plus className="h-2.5 w-2.5" />
                    </button>
                  </div>
                  <div className="flex flex-col">
                    {children.map((child, i) => (
                      <div key={child.id} style={{ marginTop: i > 0 ? ROW_GAP : 0 }}>
                        {renderNode(child, true)}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })() : (
              <div className="flex flex-row-reverse items-start">
                <div className="relative flex items-center" style={{ marginTop: getCardCenterY(children[0].id) - 8 }}>
                  <svg width={CONNECTOR_W} height="16" className="shrink-0">
                    <line x1={CONNECTOR_W} y1="8" x2="0" y2="8" stroke="hsl(var(--muted-foreground) / 0.3)" strokeWidth="1.5" strokeDasharray="4 3"
                      className="svg-connector-path"
                      style={{ '--path-length': `${CONNECTOR_W}`, strokeDasharray: `${CONNECTOR_W}`, strokeDashoffset: CONNECTOR_W } as React.CSSProperties}
                    />
                    <polygon
                      points={`${CONNECTOR_W},8 ${CONNECTOR_W - 5},5 ${CONNECTOR_W - 5},11`}
                      fill="hsl(var(--muted-foreground) / 0.3)"
                      className="svg-arrow-head"
                    />
                  </svg>
                  <div className="absolute" style={{ left: CONNECTOR_W / 2 - 8, top: 0 }}>
                    <BranchDropdownButton
                      onBranchBackward={() => { scrollIsBranch.current = true; scrollToStepId.current = addBranch(project.id, step.id); }}
                      onBranchForward={() => {
                        const { branchId, intermediateId } = addBranchForward(project.id, step.id);
                        forwardStepIds.current.add(branchId);
                        forwardStepIds.current.add(intermediateId);
                        scrollIsBranch.current = true;
                        scrollToStepId.current = branchId;
                      }}
                    />
                  </div>
                </div>
                {renderNode(children[0])}
              </div>
            )}
          </div>
        )}

        {/* Show "+" on the LEFT of leaf nodes — offers both directions */}
        {isLeaf && (
          <div className="flex flex-row-reverse items-center" style={{ marginTop: getCardCenterY(step.id) - 12 }}>
            <svg width="32" height="16" className="shrink-0">
              <line x1="32" y1="8" x2="6" y2="8" stroke="hsl(var(--muted-foreground) / 0.3)" strokeWidth="1.5" strokeDasharray="4 3" />
            </svg>
            <PlusButton onClick={() => { scrollToStepId.current = addStepAfter(project.id, step.id); }} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col bg-background">
      <style>{svgAnimStyles}</style>
      <AppHeader />
        <main className="flex-1 flex flex-col min-h-0">
          {/* Breadcrumb + actions row */}
          <div className="flex items-center justify-between px-6 py-3 border-b border-border">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="/">Synthesis Plans</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="font-semibold">{project.name}</BreadcrumbPage>
                </BreadcrumbItem>
                {project.targetCompound && (
                  <>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbPage className="text-primary font-medium">
                        {project.targetCompound}
                      </BreadcrumbPage>
                    </BreadcrumbItem>
                  </>
                )}
              </BreadcrumbList>
            </Breadcrumb>

            <div className="flex items-center gap-3">
              {selectingForRun ? (
                <>
                  <button
                    onClick={cancelSelection}
                    className="text-sm border border-border text-muted-foreground rounded px-3 py-1 hover:border-primary hover:text-primary transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={selectedStepIds.length === project.steps.length ? () => setSelectedStepIds([]) : selectAll}
                    className="text-sm border border-border text-muted-foreground rounded px-3 py-1 hover:border-primary hover:text-primary transition-colors font-medium"
                  >
                    {selectedStepIds.length === project.steps.length ? "Deselect All" : "Select All"}
                  </button>
                  <div className="flex-1" />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        disabled={selectedStepIds.length === 0}
                        className="text-sm border border-border text-muted-foreground rounded px-3 py-1 hover:border-primary hover:text-primary transition-colors font-medium disabled:opacity-50 disabled:pointer-events-none flex items-center gap-1.5"
                      >
                        <MoreHorizontal className="h-3.5 w-3.5" />
                        More
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-popover">
                      <DropdownMenuItem
                        onClick={() => {
                          navigate(`/project/${project.id}/bom`, { state: { selectedStepIds } });
                          setSelectingForRun(false);
                        }}
                      >
                        <ClipboardList className="h-4 w-4 mr-2" />
                        View BoM ({selectedStepIds.length})
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          selectedStepIds.forEach((sid) => deleteStep(project.id, sid));
                          setSelectedStepIds([]);
                          setSelectingForRun(false);
                        }}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete ({selectedStepIds.length})
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <button
                    onClick={confirmSelection}
                    disabled={selectedStepIds.length === 0}
                    className="text-sm bg-primary text-primary-foreground rounded px-3 py-1 hover:bg-primary/90 transition-colors font-medium disabled:opacity-50 disabled:pointer-events-none"
                  >
                    Create Run ({selectedStepIds.length})
                  </button>
                </>
              ) : (
                <button
                  onClick={enterSelectionMode}
                  className="text-sm border border-border text-muted-foreground rounded px-3 py-1 hover:border-primary hover:text-primary transition-colors font-medium"
                >
                  Select
                </button>
              )}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="text-sm border border-border text-muted-foreground rounded px-3 py-1 hover:border-primary hover:text-primary transition-colors font-medium flex items-center gap-1.5"
                title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
              >
                {sidebarOpen ? <PanelRightClose className="h-3.5 w-3.5" /> : <PanelRightOpen className="h-3.5 w-3.5" />}
                {sidebarOpen ? "Hide" : "Info"}
              </button>
            </div>
          </div>


        {/* Resizable canvas + sidebar */}
        <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0">
          <ResizablePanel defaultSize={75} minSize={50}>
            <div className="relative h-full">
              <div
                ref={canvasRef}
                className="h-full overflow-auto px-6 py-4 select-none"
                style={{ cursor: 'grab' }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
              >
                <div
                  className="pt-4 flex flex-col items-end min-w-max origin-top-right transition-transform duration-200"
                  style={{ transform: `scale(${zoom})`, paddingLeft: LEFT_SCROLL_PADDING }}
                >
                  {roots.length === 0 && (
                    <div className="flex items-center">
                      <PlusButton onClick={() => addStep(project.id)} />
                    </div>
                  )}
                  {roots.map((root) => (
                    <div key={root.id} className="mb-8 flex flex-row items-start">
                      {renderNode(root)}
                    </div>
                  ))}
                </div>
              </div>
              {/* Zoom controls */}
              <div className="absolute bottom-4 right-4 flex items-center gap-1 bg-card border border-border rounded-lg shadow-sm px-1 py-1 z-10">
                <button
                  disabled={zoomIndex === 0}
                  onClick={() => setZoomIndex(i => Math.max(0, i - 1))}
                  className="p-1.5 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Zoom out"
                >
                  <ZoomOut className="h-4 w-4 text-muted-foreground" />
                </button>
                <span className="text-[10px] font-medium text-muted-foreground w-8 text-center tabular-nums">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  disabled={zoomIndex === ZOOM_LEVELS.length - 1}
                  onClick={() => setZoomIndex(i => Math.min(ZOOM_LEVELS.length - 1, i + 1))}
                  className="p-1.5 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Zoom in"
                >
                  <ZoomIn className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
            </div>
          </ResizablePanel>

            {sidebarOpen && (
              <>
                <ResizableHandle />
                <ResizablePanel defaultSize={25} minSize={15} maxSize={40}>
                  <ProjectSidebar
                    project={project}
                    runs={projectRuns}
                    onNewRun={enterSelectionMode}
                  />
                </ResizablePanel>
              </>
            )}
          </ResizablePanelGroup>
        </main>
    </div>
  );
};

export default ProjectView;
